import { getConfig, getAuthClient, getTrumpfUserClient } from "@trumpf/ux.utils";
import { i18nProvider } from "@trumpf/ux.i18n";
import { h, Host, forceUpdate } from "@stencil/core";
import { headerDialogs, headerIcons } from "./constants";
import { getAppLauncherTrumpf, getLegalMenuItems } from "./utils";
import { debounce } from "../../utils/debounce";
import { isStringNotEmpty } from "../../utils/type-check";
import { getNotificationProvider } from "../../apis/notifications";
/**
 * @slot (default) - Content of the sidebar
 * @slot user-dialog - Additional info about the current user that should appear in the user dialog.
 */
export class AppHeader {
    constructor() {
        this.baseClass = "uxc-app-header";
        /** Ghost main menu element used for bounds calculation */
        this.menuMainGhost = null;
        /** Width of main menu bar */
        this.menuMainWidth = 0;
        /** Width of app header element */
        this.elementWidth = 0;
        /** I18N subscriber ID */
        this.i18nSubscriberId = "";
        /** Notifications subscriber ID */
        this.notificationsSubscriberId = "";
        /** Observer unsubscribe handlers */
        this.unsubscribeUserClient = null;
        this.debouncedResize = debounce(() => {
            this.handleBreakpoints();
            this.handleNavigationBarWidth();
        }, 50);
        /** Handle mouse clicks outside the header */
        this.handleClickOutside = (event) => {
            if (event.target.closest("uxc-app-header") !== this.element) {
                this.collapseOtherMenus();
                switch (this.activeToggle) {
                    case "":
                    case headerDialogs.NOTIFICATIONS:
                        break;
                    default:
                        this.activeToggle = "";
                        break;
                }
            }
        };
        /** Handle breakpoints */
        this.handleBreakpoints = () => {
            this.elementWidth = this.element.clientWidth;
            this.isMobile = this.elementWidth < this.breakpointMobile;
        };
        /** Internal storage for the meta navigation data */
        this.metaNavigationData = {
            titlebar: [],
            sidebar: []
        };
        this.updateMetaNavigation = () => {
            const { t } = i18nProvider;
            const { isMobile, disableHelp, disableLegalMenuItems, disableSettings, helpNavigation, settingsNavigation, toolbarNavigation, keepHelpInTitleBar, keepSettingsInTitleBar } = this;
            const metaNavigationData = {
                titlebar: [],
                sidebar: []
            };
            const helpNavigationItems = disableLegalMenuItems ? helpNavigation : [...getLegalMenuItems(), ...helpNavigation];
            const toolbarNavigationItems = [];
            if (Array.isArray(toolbarNavigation) && toolbarNavigation.length > 0) {
                toolbarNavigation.forEach((menuItem) => {
                    if (toolbarNavigationItems.length < 3 && isStringNotEmpty(menuItem.icon)) {
                        toolbarNavigationItems.push(menuItem);
                        if (isMobile === false || menuItem.toggle) {
                            metaNavigationData.titlebar.push(menuItem);
                        }
                        else {
                            metaNavigationData.sidebar.push(menuItem);
                        }
                    }
                });
            }
            if (!disableSettings && Array.isArray(settingsNavigation)) {
                const settingsMetaMenuItem = {
                    icon: headerIcons.SETTINGS,
                    slug: headerDialogs.SETTINGS,
                    label: t("app.header.settingsLabel"),
                    items: settingsNavigation
                };
                if (isMobile === false || keepSettingsInTitleBar) {
                    metaNavigationData.titlebar.push(settingsMetaMenuItem);
                }
                else {
                    metaNavigationData.sidebar.push(settingsMetaMenuItem);
                }
            }
            if (!disableHelp && Array.isArray(helpNavigation)) {
                const helpMetaMenuItem = {
                    icon: headerIcons.HELP,
                    slug: headerDialogs.HELP,
                    label: t("app.header.helpLabel"),
                    items: helpNavigationItems
                };
                if (isMobile === false || keepHelpInTitleBar) {
                    metaNavigationData.titlebar.push(helpMetaMenuItem);
                }
                else {
                    metaNavigationData.sidebar.push(helpMetaMenuItem);
                }
            }
            this.metaNavigationData = metaNavigationData;
        };
        /** Handle width of navigation bar based on available space */
        this.handleNavigationBarWidth = () => {
            if (this.menuMainGhost === null) {
                return;
            }
            if (this.disableMainNavigationBar) {
                this.isNavigationBarActive = false;
                return;
            }
            this.menuMainWidth = this.menuMainGhost.offsetWidth;
            const menuApps = this.element.querySelector("." + this.baseClass + "__app-launcher") || null;
            const menuMeta = this.element.querySelector("." + this.baseClass + "__meta-navigation") || null;
            const elementWidth = this.element.clientWidth;
            if (elementWidth >= this.breakpointMobile) {
                const menuAppsWidth = menuApps !== null ? menuApps.offsetWidth : 0;
                const menuMetaWidth = menuMeta !== null ? menuMeta.offsetWidth : 0;
                const menuMainWidthDynamic = elementWidth - menuAppsWidth - menuMetaWidth;
                this.isNavigationBarActive = menuMainWidthDynamic >= this.menuMainWidth;
            }
            else {
                this.isNavigationBarActive = false;
            }
        };
        this.handleNotifications = () => {
            this.notificationsCounter = getNotificationProvider().counter;
        };
        /** Use TrumpfUserClient to fetch userData from API */
        this.initTrumpfUserClient = async () => {
            const { websiteBaseUrl, websitePaths } = getConfig();
            this.removeTrumpfUserClient();
            const trumpfUserClient = getTrumpfUserClient();
            this.unsubscribeUserClient = trumpfUserClient.subscribe(() => {
                this.activeUserData = trumpfUserClient.getBaseUser();
            });
            await trumpfUserClient.getUserData();
            this.activeUserData = trumpfUserClient.getBaseUser();
            this.userProfileUrl = `${websiteBaseUrl}${websitePaths.userProfile}`;
        };
        this.removeTrumpfUserClient = () => {
            if (this.unsubscribeUserClient !== null) {
                this.unsubscribeUserClient();
                this.unsubscribeUserClient = null;
            }
        };
        this.handleLogout = () => {
            this.logoutClicked.emit();
            if (this.unsubscribeUserClient !== null) {
                this.activeUserData = null;
                getAuthClient().deleteAccessToken();
            }
        };
        this.handleMainMenuChange = (event) => {
            const { current, element } = event.detail;
            if (!element || element.href || element.toggle) {
                return;
            }
            this.activeMenuSlug = current;
            if (this.activeToggle === headerDialogs.BURGER_MENU) {
                return;
            }
            this.activeToggle = "";
        };
        this.handleMainMenuClick = (event) => {
            const menuItem = event.target.closest(".uic-menu-item");
            const menu = event.target.closest(".uic-menu");
            this.collapseOtherMenus(menu);
            if (menuItem.toggle) {
                return;
            }
            if (this.activeToggle !== "" &&
                menuItem.level === 0 &&
                Array.isArray(menuItem.items) &&
                menuItem.items.length > 0 &&
                !menuItem.expanded) {
                window.setTimeout(() => {
                    menuItem.expanded = true;
                }, 20);
            }
            this.activeToggle = "";
        };
        this.handleMainMenuToggle = (event) => {
            const { element, selected, slug } = event.detail;
            if (element.level === 0) {
                this.activeToggle = selected ? slug : "";
            }
        };
        this.handleToggleMenuChange = (event) => {
            const { current } = event.detail;
            this.activeToggle = current;
            if (this.activeToggle !== "") {
                this.collapseOtherMenus(event.target);
            }
            this.menuToggle.emit(event.detail.current);
        };
        this.collapseOtherMenus = (menu) => {
            this.element.querySelectorAll(`.${this.baseClass}__inner .uic-menu`).forEach((el) => {
                if (el !== menu) {
                    el.collapseAll();
                }
            });
        };
        this.renderMainNavigationBurger = () => {
            const { t } = i18nProvider;
            const { baseClass, activeToggle, handleToggleMenuChange, disableMenuLabel } = this;
            return (h("uic-menu", { class: `${baseClass}__main-navigation-burger`, name: "app-header-toggle-nav", "auto-collapse": false, current: activeToggle, "context-element-selector": "uxc-app-header", items: [
                    {
                        icon: headerIcons.BURGER_MENU,
                        slug: headerDialogs.BURGER_MENU,
                        label: disableMenuLabel ? "" : t("app.header.menuLabel")
                    }
                ], onUicMenuChange: handleToggleMenuChange, toggle: true }));
        };
        this.renderMenuSidebar = (position) => {
            const { baseClass, activeToggle, activeMenuSlug, metaNavigationData } = this;
            const metaMenuItems = metaNavigationData.sidebar;
            return (h("uic-sidebar", { class: `${baseClass}__menu-sidebar`, opened: activeToggle === headerDialogs.BURGER_MENU, side: position, alignHeader: true }, h("div", { class: `${baseClass}__sidebar-navigation` }, h("uic-menu", { class: `${baseClass}__sidebar-navigation-main`, name: "app-header-main-nav", current: activeMenuSlug, "context-element-selector": "uxc-app-header", items: this.mainNavigation, onUicMenuChange: this.handleMainMenuChange, vertical: true })), metaMenuItems.length > 0 && (h("div", { class: `${baseClass}__sidebar-meta` }, h("uic-menu", { class: `${baseClass}__sidebar-navigation-meta`, name: "app-header-main-nav", current: activeMenuSlug, "context-element-selector": "uxc-app-header", items: metaMenuItems, onUicMenuChange: this.handleMainMenuChange, vertical: true })))));
        };
        this.renderMetaNav = () => {
            const { t } = i18nProvider;
            const { baseClass, activeToggle, disableNotifications, disableProfile, activeMenuSlug, notificationsCounter, metaNavigationData, handleMainMenuClick, handleMainMenuChange, handleMainMenuToggle, handleToggleMenuChange } = this;
            const metaMenuItems = metaNavigationData.titlebar;
            const showNotifications = !disableNotifications;
            const showProfile = !disableProfile;
            return (h("div", { class: `${baseClass}__meta-navigation` }, h("div", { class: `${baseClass}__menu-container` }, metaMenuItems.length > 0 && (h("uic-menu", { name: "app-header-main-nav", class: `${baseClass}__meta-navigation-bar`, items: metaMenuItems, current: activeMenuSlug, "context-element-selector": "uxc-app-header", onUicMenuChange: handleMainMenuChange, onUicMenuToggle: handleMainMenuToggle, onClick: handleMainMenuClick }))), h("div", { class: `${baseClass}__menu-container` }, (showNotifications || showProfile) && (h("uic-menu", { name: "app-header-toggle-nav", class: `${baseClass}__toggle-nav`, current: activeToggle, "context-element-selector": "uxc-app-header", onUicMenuChange: handleToggleMenuChange, toggle: true }, showNotifications && (h("uic-menu-item", { label: t("app.header.notificationsLabel"), slug: headerDialogs.NOTIFICATIONS, badge: notificationsCounter, icon: headerIcons.NOTIFICATIONS })), showProfile && (h("uic-menu-item", { icon: headerIcons.USER, label: t("app.header.userLabel"), slug: headerDialogs.USER })))))));
        };
        this.appsNavigation = undefined;
        this.mainNavigation = [];
        this.settingsNavigation = [];
        this.helpNavigation = [];
        this.toolbarNavigation = [];
        this.disableApps = false;
        this.disableSettings = false;
        this.disableHelp = false;
        this.disableProfile = false;
        this.disableNotifications = false;
        this.disableMenuLabel = false;
        this.disableMainNavigationBar = false;
        this.disableLegalMenuItems = false;
        this.breakpointMobile = 1024;
        this.keepSettingsInTitleBar = false;
        this.keepHelpInTitleBar = false;
        this.menuPosition = "left";
        this.activeMenuSlug = "";
        this.activeToggle = "";
        this.userData = null;
        this.userProfileUrl = null;
        this.notificationsCounter = undefined;
        this.language = undefined;
        this.isNavigationBarActive = undefined;
        this.isMobile = undefined;
        this.activeUserData = null;
        this.sidebarNotifications = null;
    }
    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => {
            this.updateMetaNavigation();
            forceUpdate(this);
        });
        this.notificationsSubscriberId = getNotificationProvider().subscribe(this.handleNotifications);
        if (this.userData === null && !this.disableProfile) {
            this.initTrumpfUserClient();
        }
        document.addEventListener("click", this.handleClickOutside);
    }
    disconnectedCallback() {
        i18nProvider.unsubscribe(this.i18nSubscriberId);
        getNotificationProvider().unsubscribe(this.notificationsSubscriberId);
        this.removeTrumpfUserClient();
        document.removeEventListener("click", this.handleClickOutside);
    }
    componentWillLoad() {
        if (this.disableMainNavigationBar) {
            this.isNavigationBarActive = false;
        }
        if (this.notificationsCounter === undefined) {
            this.notificationsCounter = getNotificationProvider().counter;
        }
        this.setLanguage();
        this.setUserData();
        this.updateMetaNavigation();
    }
    componentDidLoad() {
        this.debouncedResize();
    }
    /** Handle language prop change */
    setLanguage() {
        if (isStringNotEmpty(this.language)) {
            i18nProvider.changeLanguage(this.language);
        }
    }
    /** Handle userData */
    setUserData() {
        if (this.userData === null) {
            this.activeUserData = null;
            if (!this.disableProfile) {
                this.initTrumpfUserClient();
            }
        }
        else {
            this.removeTrumpfUserClient();
            this.activeUserData = this.userData;
        }
    }
    /** Handle activeToggle change */
    handleActiveToggle() {
        if (this.sidebarNotifications !== null) {
            this.sidebarNotifications.opened = this.activeToggle === headerDialogs.NOTIFICATIONS;
        }
        this.element.querySelectorAll(`uic-menu-item[level="0"][toggle]`).forEach((item) => {
            if (!this.activeToggle || item.slug !== this.activeToggle) {
                item.selected = false;
            }
        });
    }
    /** Handle breakpoint settings change */
    handleBreakpointSettings() {
        this.debouncedResize();
    }
    /** Handle meta navigation changes */
    handleMetaNavigationData() {
        this.updateMetaNavigation();
    }
    /** Listen to window resize events */
    onResize() {
        this.debouncedResize();
    }
    /** Listen to uicAppSidebarNotifications event */
    handleAppSidebarNotificationsMounted(event) {
        const sidebar = this.element.parentElement.querySelector("uxc-app-sidebar-notifications");
        this.sidebarNotifications = sidebar || event.target;
        if (this.sidebarNotifications && this.sidebarNotifications.opened && !this.disableNotifications) {
            this.activeToggle = headerDialogs.NOTIFICATIONS;
        }
        this.debouncedResize();
    }
    render() {
        const { t } = i18nProvider;
        const { baseClass, disableApps, disableHelp, disableProfile, disableSettings, isMobile, isNavigationBarActive, keepHelpInTitleBar, keepSettingsInTitleBar, menuPosition, activeUserData, userProfileUrl, activeMenuSlug, activeToggle, handleLogout, handleMainMenuChange, handleMainMenuClick } = this;
        const appLauncherItems = disableApps
            ? this.appsNavigation || []
            : [...(this.appsNavigation || []), ...getAppLauncherTrumpf()];
        const showAppHeaderContent = isMobile !== undefined && isNavigationBarActive !== undefined;
        const hideNavigationBurger = isNavigationBarActive !== false ||
            (this.mainNavigation.length <= 0 && (disableSettings || keepSettingsInTitleBar) && (disableHelp || keepHelpInTitleBar));
        return (h(Host, { key: '8a4eb5a3007bcd16a54dfd3da85aaf61961a0da0', class: {
                [`${baseClass}`]: true,
                [`${baseClass}--has-navigation-bar`]: isNavigationBarActive,
                [`${baseClass}--is-active`]: showAppHeaderContent
            } }, h("div", { key: '68292268b3ab49bda5660382ec4ffe7925e22fc1', class: `${baseClass}__inner` }, h("div", { key: 'c624688fc41e84ac41a24741f4f1ccdc8905838c', class: `${baseClass}__navigation-left` }, h("div", { key: '64829a93a494b19fee565e59677806c285d4876a', class: `${baseClass}__app-launcher` }, h("uic-menu", { key: 'c140550d2eabaf9b965f2168a5bfcd1fd42eb92b', name: "app-header-main-nav", current: activeMenuSlug, class: `${baseClass}__apps-nav`, "context-element-selector": "uxc-app-header", onUicMenuChange: handleMainMenuChange, onClick: handleMainMenuClick }, h("uic-menu-item", { key: '61a8aaa9073c4bd2a68b72d0c19fb5f42d8bb285', class: `${baseClass}__icon-app-launcher`, icon: headerIcons.APP_LAUNCHER, slug: headerDialogs.APP_LAUNCHER, items: appLauncherItems, selected: activeToggle === headerDialogs.APP_LAUNCHER, expanded: activeToggle === headerDialogs.APP_LAUNCHER }))), h("div", { key: '313668def522c268c2007e54659c2b198e30fda5', class: `${baseClass}__main-navigation` }, !hideNavigationBurger && menuPosition === "left" && this.renderMainNavigationBurger(), isNavigationBarActive === true && (h("uic-menu", { key: '59a0e412e6f2e0fac00a2a43a5a9f5758ead36a4', class: `${baseClass}__main-navigation-bar`, name: "app-header-main-nav", current: activeMenuSlug, "context-element-selector": "uxc-app-header", items: this.mainNavigation, onUicMenuChange: handleMainMenuChange, onClick: handleMainMenuClick })))), h("div", { key: 'b62860bcd0a4a5f2d9ccb1e6bb6c60f2a83fcd7a', class: `${baseClass}__navigation-right` }, this.renderMetaNav(), !hideNavigationBurger && menuPosition === "right" && this.renderMainNavigationBurger())), h("uic-flyout", { key: '40c93268e3866ba2c5e456a4edf363cefde49215', position: "right", name: headerDialogs.USER, opened: !disableProfile && activeToggle === headerDialogs.USER }, h("div", { key: '658988a2199c68b15a29b7b0213a13b2aff222f6', slot: "content" }, h("uic-app-user-info", { key: 'c74115992fde02db54c4b011ebdc5a3a765ce498', noDataText: t("app.header.noUserDataLabel"), userData: activeUserData, userProfileText: t("app.header.userProfileLinkText"), userProfileUrl: userProfileUrl }, h("slot", { key: '75c72daad9031d814c71cd405be8a287926b3b35', name: "user-dialog" }))), activeUserData !== null && (h("uic-button", { key: '7cc23aaca02899fcbd74ece0e0e99182aafc0ea0', slot: "footer", variant: "footer", onClick: handleLogout }, t("app.header.logoutButton"))), h("uic-button", { key: '1105138d2c1aec53148097aedc4827fafbf8a8df', slot: "footer", variant: "footer-primary", onClick: () => (this.activeToggle = "") }, t("app.header.closeButton"))), isNavigationBarActive === false && this.renderMenuSidebar(menuPosition), h("uic-menu", { key: '4daafc446ce68a3973d92d8912d8812e03ec9d08', name: "main-navigation-hidden", class: `${baseClass}__main-navigation-hidden`, items: this.mainNavigation, ref: (el) => (this.menuMainGhost = el) })));
    }
    static get is() { return "uxc-app-header"; }
    static get originalStyleUrls() {
        return {
            "$": ["app-header.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["app-header.css"]
        };
    }
    static get properties() {
        return {
            "appsNavigation": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "MenuItemType[]",
                    "resolved": "MenuItemType[]",
                    "references": {
                        "MenuItemType": {
                            "location": "import",
                            "path": "@trumpf/ux.ui-community/dist/types/components/menu/interface",
                            "id": "../../node_modules/@trumpf/ux.ui-community/dist/types/components/menu/interface.d.ts::MenuItemType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Apps listed in the app-launcher sidebar"
                }
            },
            "mainNavigation": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "MenuItemType[]",
                    "resolved": "MenuItemType[]",
                    "references": {
                        "MenuItemType": {
                            "location": "import",
                            "path": "@trumpf/ux.ui-community/dist/types/components/menu/interface",
                            "id": "../../node_modules/@trumpf/ux.ui-community/dist/types/components/menu/interface.d.ts::MenuItemType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Main navigation menu items"
                },
                "defaultValue": "[]"
            },
            "settingsNavigation": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "MenuItemType[]",
                    "resolved": "MenuItemType[]",
                    "references": {
                        "MenuItemType": {
                            "location": "import",
                            "path": "@trumpf/ux.ui-community/dist/types/components/menu/interface",
                            "id": "../../node_modules/@trumpf/ux.ui-community/dist/types/components/menu/interface.d.ts::MenuItemType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Settings navigation menu items"
                },
                "defaultValue": "[]"
            },
            "helpNavigation": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "MenuItemType[]",
                    "resolved": "MenuItemType[]",
                    "references": {
                        "MenuItemType": {
                            "location": "import",
                            "path": "@trumpf/ux.ui-community/dist/types/components/menu/interface",
                            "id": "../../node_modules/@trumpf/ux.ui-community/dist/types/components/menu/interface.d.ts::MenuItemType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Help navigation menu items"
                },
                "defaultValue": "[]"
            },
            "toolbarNavigation": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "MenuItemType[]",
                    "resolved": "MenuItemType[]",
                    "references": {
                        "MenuItemType": {
                            "location": "import",
                            "path": "@trumpf/ux.ui-community/dist/types/components/menu/interface",
                            "id": "../../node_modules/@trumpf/ux.ui-community/dist/types/components/menu/interface.d.ts::MenuItemType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Custom toolbar navigation menu items"
                },
                "defaultValue": "[]"
            },
            "disableApps": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set to `true` to turn off the TRUMPF apps in the app-launcher"
                },
                "attribute": "disable-apps",
                "reflect": false,
                "defaultValue": "false"
            },
            "disableSettings": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set to `true` to turn off the settings icon and menu"
                },
                "attribute": "disable-settings",
                "reflect": false,
                "defaultValue": "false"
            },
            "disableHelp": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set to `true` to turn off the help icon and menu"
                },
                "attribute": "disable-help",
                "reflect": false,
                "defaultValue": "false"
            },
            "disableProfile": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set to `true` to turn off the user icon and flyout"
                },
                "attribute": "disable-profile",
                "reflect": false,
                "defaultValue": "false"
            },
            "disableNotifications": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set to `true` to turn off notifications"
                },
                "attribute": "disable-notifications",
                "reflect": false,
                "defaultValue": "false"
            },
            "disableMenuLabel": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set to `true` to turn off the burger menu button label"
                },
                "attribute": "disable-menu-label",
                "reflect": false,
                "defaultValue": "false"
            },
            "disableMainNavigationBar": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set to `true` to always display a burger menu instead of a navigation bar"
                },
                "attribute": "disable-main-navigation-bar",
                "reflect": false,
                "defaultValue": "false"
            },
            "disableLegalMenuItems": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set to `true` to turn off legal notice & privacy policy links within help navigation"
                },
                "attribute": "disable-legal-menu-items",
                "reflect": false,
                "defaultValue": "false"
            },
            "breakpointMobile": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Breakpoint used to decide between mobile or desktop view"
                },
                "attribute": "breakpoint-mobile",
                "reflect": false,
                "defaultValue": "1024"
            },
            "keepSettingsInTitleBar": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If set to `true`, the settings icon will remain in the title bar on screen widths below `breakpointMobile`"
                },
                "attribute": "keep-settings-in-title-bar",
                "reflect": false,
                "defaultValue": "false"
            },
            "keepHelpInTitleBar": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If set to `true`, the help icon will remain in the title bar on screen widths below `breakpointMobile`"
                },
                "attribute": "keep-help-in-title-bar",
                "reflect": false,
                "defaultValue": "false"
            },
            "menuPosition": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"left\" | \"right\"",
                    "resolved": "\"left\" | \"right\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The horizontal placement of the burger menu button and dropdown"
                },
                "attribute": "menu-position",
                "reflect": false,
                "defaultValue": "\"left\""
            },
            "activeMenuSlug": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Slug of the currently active menu item"
                },
                "attribute": "active-menu-slug",
                "reflect": true,
                "defaultValue": "\"\""
            },
            "activeToggle": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Slug of the currently active toggle menu item"
                },
                "attribute": "active-toggle",
                "reflect": true,
                "defaultValue": "\"\""
            },
            "userData": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "BaseUserModel | null",
                    "resolved": "BaseUserModel",
                    "references": {
                        "BaseUserModel": {
                            "location": "import",
                            "path": "@trumpf/ux.utils",
                            "id": "../../node_modules/@trumpf/ux.utils/dist/types/index.d.ts::BaseUserModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Custom user data if the MyTRUMPF user API is not used"
                },
                "defaultValue": "null"
            },
            "userProfileUrl": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string | null",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Link to the TRUMPF user profile"
                },
                "attribute": "user-profile-url",
                "reflect": false,
                "defaultValue": "null"
            },
            "notificationsCounter": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Counter displayed above the notifications icon"
                },
                "attribute": "notifications-counter",
                "reflect": false
            },
            "language": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Setting the language prop on the header will change the global i18n language"
                },
                "attribute": "language",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "isNavigationBarActive": {},
            "isMobile": {},
            "activeUserData": {},
            "sidebarNotifications": {}
        };
    }
    static get events() {
        return [{
                "method": "menuWillChange",
                "name": "uicMenuChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when a menu item is selected or deselected"
                },
                "complexType": {
                    "original": "{\n        name: string;\n        current: string | null;\n    }",
                    "resolved": "{ name: string; current: string; }",
                    "references": {}
                }
            }, {
                "method": "logoutClicked",
                "name": "uicLogout",
                "bubbles": true,
                "cancelable": false,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the logout button was clicked"
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }, {
                "method": "menuToggle",
                "name": "uicMenuToggle",
                "bubbles": true,
                "cancelable": false,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the menu toggles"
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "element"; }
    static get watchers() {
        return [{
                "propName": "language",
                "methodName": "setLanguage"
            }, {
                "propName": "userData",
                "methodName": "setUserData"
            }, {
                "propName": "activeToggle",
                "methodName": "handleActiveToggle"
            }, {
                "propName": "breakpointMobile",
                "methodName": "handleBreakpointSettings"
            }, {
                "propName": "disableHelp",
                "methodName": "handleMetaNavigationData"
            }, {
                "propName": "disableLegalMenuItems",
                "methodName": "handleMetaNavigationData"
            }, {
                "propName": "disableSettings",
                "methodName": "handleMetaNavigationData"
            }, {
                "propName": "helpNavigation",
                "methodName": "handleMetaNavigationData"
            }, {
                "propName": "settingsNavigation",
                "methodName": "handleMetaNavigationData"
            }, {
                "propName": "toolbarNavigation",
                "methodName": "handleMetaNavigationData"
            }, {
                "propName": "isMobile",
                "methodName": "handleMetaNavigationData"
            }, {
                "propName": "keepHelpInTitleBar",
                "methodName": "handleMetaNavigationData"
            }, {
                "propName": "keepSettingsInTitleBar",
                "methodName": "handleMetaNavigationData"
            }];
    }
    static get listeners() {
        return [{
                "name": "resize",
                "method": "onResize",
                "target": "window",
                "capture": false,
                "passive": true
            }, {
                "name": "uicAppSidebarNotifications",
                "method": "handleAppSidebarNotificationsMounted",
                "target": "window",
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=app-header.js.map
